'use client';

import { Conversation, Transcript } from '@/types/pulsedb';
import { useSupaClient } from '@/utils/clerk/client';
import { useDependentSWR } from '@/utils/hooks/useDependentSWR';
import { useVideoUrls, useTranscriptMarkers } from '@/utils/hooks/video';
import { conversationDuration } from '@/utils/pulse/interview';
import { recordingUrls } from '@/utils/s3';
import { getTranscript } from '@/utils/supabase/queries';
import { BaseVideoPlayer } from './BaseVideoPlayer';

export interface ConversationVideoPlayerProps {
  /** Conversation to play */
  conversation: Conversation | null;
  /** Optional pre-fetched transcript (for shared views without auth) */
  transcript?: Transcript | null;
  /** Event index to start playback from */
  startEventIndex?: number;
  /** Auto-start playback */
  autoPlay?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether displayed in a preview/card (no volume control) */
  inPreviewDisplay?: boolean;
  /** Whether to show the download control */
  showDownload?: boolean;
  /** Message to display in a loading card while video loads (null = default skeleton) */
  loadingMessage?: boolean;
}

/**
 * Video player for conversation recordings.
 * Handles transcript fetching and topic marker generation.
 */
export function ConversationVideoPlayer({
  conversation,
  transcript: transcriptProp,
  startEventIndex,
  autoPlay = false,
  className,
  inPreviewDisplay = false,
  showDownload = true,
  loadingMessage = false
}: ConversationVideoPlayerProps) {
  const supaClient = useSupaClient();

  // Fetch URLs
  const {
    recordingUrl,
    thumbnailUrl,
    loading: urlsLoading
  } = useVideoUrls({
    fetchUrls: () => recordingUrls('conversation', conversation?.id ?? 0),
    deps: [conversation?.id]
  });

  // Fetch transcript if not provided
  const shouldFetchTranscript = !transcriptProp && supaClient && conversation;
  const { data: fetchedTranscript } = useDependentSWR(
    shouldFetchTranscript ? `/transcript/${conversation?.id}` : null,
    () => getTranscript(supaClient!, conversation!.id),
    [supaClient, conversation]
  );
  const transcript = transcriptProp ?? fetchedTranscript;

  // Generate markers from transcript
  const { markers, startTime } = useTranscriptMarkers({
    transcript,
    startEventIndex
  });

  // Calculate duration
  const duration = conversation
    ? conversationDuration(conversation)
    : undefined;

  if (!conversation) {
    return null;
  }

  const controls = [
    'play-large',
    'play',
    'progress',
    'current-time',
    'fullscreen',
    'mute',
    ...(inPreviewDisplay ? [] : ['volume', 'settings']),
    ...(showDownload ? ['download'] : [])
  ];
  return (
    <BaseVideoPlayer
      recordingUrl={recordingUrl}
      thumbnailUrl={thumbnailUrl}
      loading={urlsLoading}
      loadingMessage={loadingMessage && urlsLoading ? 'Loading...' : undefined}
      isProcessing={conversation.processing}
      duration={duration}
      showDurationBadge
      markers={markers}
      controls={controls}
      enableSpeedControls={!inPreviewDisplay}
      autoSeekTo={startTime}
      autoPlay={autoPlay}
      enableTimestampJump
      centeredVideoStyle
      className={className}
    />
  );
}

// Default export for backwards compatibility with dynamic import
export default ConversationVideoPlayer;
